import { exampleText, money } from './format';

const STATUS_LABEL = { pass: 'OK', warn: 'Review', fail: 'Action needed', na: 'Not set up', good: 'Good', fair: 'Watch', poor: 'Concern', info: 'Info' };
const STATUS_ORDER = { fail: 0, warn: 1, pass: 2, na: 3 };

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function reportDate(value) {
  const date = value ? new Date(value) : new Date();
  return date.toLocaleString('en-GB', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  });
}

function filenamePart(value) {
  const cleaned = String(value || 'organisation')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return cleaned || 'organisation';
}

function label(value) {
  return String(value || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, c => c.toUpperCase());
}

function safeNumber(value) {
  return typeof value === 'number' && Number.isFinite(value)
    ? value.toLocaleString('en-GB', { maximumFractionDigits: 2 })
    : String(value ?? '—');
}

function statusText(status) {
  return STATUS_LABEL[status] || label(status || '');
}

function downloadHtml(filename, title, content) {
  const documentHtml = `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)}</title>
  <style>
    :root { color-scheme: light; }
    * { box-sizing: border-box; }
    body { margin: 0; color: #1f2933; background: #f3f5f4; font-family: Arial, Helvetica, sans-serif; font-size: 14px; line-height: 1.45; }
    main { max-width: 980px; margin: 28px auto; padding: 38px 42px; background: #fff; box-shadow: 0 1px 5px rgba(0,0,0,.12); }
    header { border-bottom: 2px solid #1d9e75; padding-bottom: 18px; margin-bottom: 24px; }
    h1 { margin: 0 0 6px; font-size: 25px; color: #17212b; }
    h2 { margin: 28px 0 12px; font-size: 18px; color: #17212b; }
    h3 { margin: 0; font-size: 15px; color: #17212b; }
    p { margin: 7px 0; }
    .muted { color: #66737d; font-size: 12px; }
    .brand { color: #4935a8; font-weight: 700; letter-spacing: .02em; }
    .meta { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin-top: 14px; }
    .meta strong { color: #17212b; }
    .summary { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 10px; margin: 18px 0 24px; }
    .summary-card { border: 1px solid #d9e3df; border-radius: 8px; padding: 12px; background: #f9fbfa; }
    .summary-label { color: #66737d; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
    .summary-value { margin-top: 4px; font-size: 24px; font-weight: 700; }
    .good { color: #4935a8; } .fair { color: #9a6100; } .poor, .fail { color: #a32d2d; } .warn { color: #9a6100; } .pass { color: #4935a8; } .na { color: #66737d; }
    .finding, .insight { border: 1px solid #dfe5e2; border-left: 4px solid #aab6b1; border-radius: 7px; padding: 14px 16px; margin: 10px 0; break-inside: avoid; }
    .finding.fail { border-left-color: #a32d2d; } .finding.warn { border-left-color: #ba7517; } .finding.pass { border-left-color: #1d9e75; }
    .finding-top, .insight-top { display: flex; justify-content: space-between; align-items: baseline; gap: 14px; }
    .pill { padding: 2px 8px; border-radius: 12px; font-size: 11px; font-weight: 700; white-space: nowrap; background: #eef1ef; }
    .pill.fail, .pill.poor { background: #fcebea; } .pill.warn, .pill.fair { background: #faeeda; } .pill.pass, .pill.good { background: #e1f5ee; }
    .severity { color: #66737d; font-size: 11px; text-transform: capitalize; }
    .why { margin-top: 10px; color: #4a5861; }
    ul { margin: 8px 0 0 18px; padding: 0; color: #4a5861; }
    li { margin: 4px 0; }
    .note { margin: 10px 0; padding: 10px 12px; border-radius: 6px; color: #755000; background: #fff6e4; }
    .headline { margin: 4px 0 8px; font-size: 22px; font-weight: 700; }
    .method { margin-top: 10px; padding-top: 10px; border-top: 1px solid #edf0ee; color: #66737d; font-size: 12px; }
    table { width: 100%; border-collapse: collapse; margin-top: 10px; font-size: 12px; }
    th, td { padding: 7px 8px; text-align: left; vertical-align: top; border-bottom: 1px solid #e8edeb; }
    th { width: 34%; color: #52606a; font-weight: 600; }
    pre { margin: 10px 0 0; padding: 10px; overflow-x: auto; white-space: pre-wrap; word-break: break-word; color: #34434c; background: #f6f8f7; border-radius: 5px; font-size: 11px; }
    footer { margin-top: 34px; padding-top: 12px; border-top: 1px solid #dfe5e2; color: #66737d; font-size: 11px; }
    @media print { body { background: #fff; } main { max-width: none; margin: 0; padding: 0; box-shadow: none; } }
    @media (max-width: 600px) { main { margin: 0; padding: 24px 18px; } .summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .meta { grid-template-columns: 1fr; } }
  </style>
</head>
<body><main>${content}<footer>Generated by <span class="brand">Assess &amp; Reconcile</span>. This report is a read-only summary of the completed check and should be reviewed with the underlying Xero records.</footer></main></body>
</html>`;
  const blob = new Blob([documentHtml], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function reportHeader(title, clientName, completedAt) {
  const dateLabel = completedAt ? 'Completed' : 'Generated';
  return `<header>
    <div class="brand">ASSESS &amp; RECONCILE</div>
    <h1>${escapeHtml(title)}</h1>
    <div class="meta"><div><strong>Organisation:</strong> ${escapeHtml(clientName || 'Connected organisation')}</div><div><strong>${dateLabel}:</strong> ${escapeHtml(reportDate(completedAt))}</div></div>
  </header>`;
}

function reportNotes(result) {
  const notes = [];
  const unavailable = [...new Set((result.unavailable || []).map(item => item.reason))];
  if (unavailable.length) notes.push('Some Xero report data was unavailable, so related checks or insights may be marked as unavailable. Reconnect Xero to grant the required report access.');
  if ((result.truncated || []).length) notes.push(`Large organisation notice: only the most recent items were included for ${result.truncated.join(', ')}.`);
  return notes.map(note => `<div class="note">${escapeHtml(note)}</div>`).join('');
}

function resultSummary(result) {
  const findings = result.findings || [];
  const actionNeeded = findings.filter(f => f.status === 'fail').length;
  const reviewNeeded = findings.filter(f => f.status === 'warn').length;
  const transactions = (result.counts?.bankTransactions || 0) + (result.counts?.invoices || 0);
  const rating = result.rating || 'na';
  const score = result.score === null || result.score === undefined ? '—' : `${result.score}%`;
  return `<section class="summary">
    <div class="summary-card"><div class="summary-label">Health score</div><div class="summary-value ${escapeHtml(rating)}">${escapeHtml(score)}</div></div>
    <div class="summary-card"><div class="summary-label">Action needed</div><div class="summary-value fail">${actionNeeded}</div></div>
    <div class="summary-card"><div class="summary-label">To review</div><div class="summary-value warn">${reviewNeeded}</div></div>
    <div class="summary-card"><div class="summary-label">Transactions checked</div><div class="summary-value">${transactions}</div></div>
  </section>`;
}

function healthFinding(finding) {
  const examples = (finding.examples || []).map(example => `<li>${escapeHtml(exampleText(example))}</li>`).join('');
  return `<article class="finding ${escapeHtml(finding.status)}">
    <div class="finding-top"><div><span class="pill ${escapeHtml(finding.status)}">${escapeHtml(statusText(finding.status))}</span> <strong>${escapeHtml(finding.title)}</strong></div><span class="severity">${escapeHtml(finding.severity || '')}</span></div>
    <p>${escapeHtml(finding.summary || '')}${finding.value && finding.status !== 'pass' ? ` (${escapeHtml(money(finding.value))})` : ''}</p>
    ${finding.why ? `<p class="why"><strong>Why it matters:</strong> ${escapeHtml(finding.why)}</p>` : ''}
    ${examples ? `<ul>${examples}</ul>` : ''}
  </article>`;
}

export function downloadHealthReport(result, clientName, completedAt = null) {
  const findings = (result.findings || []).slice().sort((a, b) =>
    (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) || String(a.title).localeCompare(String(b.title)));
  const content = `${reportHeader('Bookkeeping Health Report', clientName, completedAt)}${resultSummary(result)}${reportNotes(result)}
    <h2>Check findings</h2>
    <p class="muted">All completed checks are included below, ordered with action-needed items first.</p>
    ${findings.map(healthFinding).join('') || '<p>No check findings were returned.</p>'}`;
  downloadHtml(`health-report-${filenamePart(clientName)}.html`, 'Bookkeeping Health Report', content);
}

function insightDetail(data) {
  const entries = Object.entries(data || {}).filter(([, value]) => value !== undefined && value !== null && value !== '');
  if (!entries.length) return '';
  const rows = entries.map(([key, value]) => {
    const display = typeof value === 'object' ? `<pre>${escapeHtml(JSON.stringify(value, null, 2))}</pre>` : escapeHtml(safeNumber(value));
    return `<tr><th>${escapeHtml(label(key))}</th><td>${display}</td></tr>`;
  }).join('');
  return `<table><tbody>${rows}</tbody></table>`;
}

function insightCard(insight) {
  return `<article class="insight">
    <div class="insight-top"><h3>${escapeHtml(insight.title)}</h3><span class="pill ${escapeHtml(insight.status)}">${escapeHtml(statusText(insight.status))}</span></div>
    <div class="headline ${escapeHtml(insight.status)}">${escapeHtml(insight.headline || '—')}</div>
    <p>${escapeHtml(insight.summary || '')}</p>
    ${insightDetail(insight.data)}
    ${insight.method ? `<p class="method"><strong>Method:</strong> ${escapeHtml(insight.method)}</p>` : ''}
  </article>`;
}

export function downloadInsightsReport(result, clientName, completedAt = null) {
  const insights = result.insights || [];
  const content = `${reportHeader('Financial Insights Report', clientName, completedAt)}${resultSummary(result)}${reportNotes(result)}
    <h2>Financial insights</h2>
    <p class="muted">Figures are estimates derived from the completed Xero data review. They are not a substitute for statutory accounts, tax advice, or a formal valuation.</p>
    ${insights.map(insightCard).join('') || '<p>No insights were returned.</p>'}`;
  downloadHtml(`insights-report-${filenamePart(clientName)}.html`, 'Financial Insights Report', content);
}
